import { StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';

// Round for display so float noise (46.51999999999999) never overflows a narrow PDF cell.
const r2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

const fuelMasterData = [
    { type: "Diesel/Gas Oil", reference: "ISO 8217 Grades DMX through DMB", lcv: 42700, carbon: 0.8744, cf: 3.206 },
    { type: "Light Fuel Oil (LFO)", reference: "ISO 8217 Grades RMA through RMD", lcv: 41200, carbon: 0.8594, cf: 3.151 },
    { type: "Heavy Fuel Oil (HFO)", reference: "ISO 8217 Grades RME thorugh RMK", lcv: 40200, carbon: 0.8493, cf: 3.114 },
    { type: "Liquified Petroleum Gas (Propane)", reference: "Propane", lcv: 46300, carbon: 0.8182, cf: 3 },
    { type: "Liquified Petroleum Gas (Butane)", reference: "Butane", lcv: 45700, carbon: 0.8264, cf: 3.03 },
    { type: "Liquified Natural Gas", reference: "LNG", lcv: 48000, carbon: 0.75, cf: 2.75 },
    { type: "Methanol", reference: "Methanol", lcv: 19900, carbon: 0.375, cf: 1.375 },
    { type: "Ethanol", reference: "Ethanol", lcv: 26800, carbon: 0.5217, cf: 1.913 }
];

const getCfValue = (fuelType) => {
    const fuel = fuelMasterData.find(f => f.type === fuelType);
    return fuel ? fuel.cf : '-';
};

const styles = StyleSheet.create({
    page: {
        padding: 15,
        paddingTop: 10,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#ffffff'
    },
    container: {
        border: '1px solid #cbd5e1',
        borderRadius: 4,
    },
    header: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        color: '#1e293b'
    },
    topSection: {
        flexDirection: 'row',
        borderBottom: '1px solid #cbd5e1'
    },
    leftSection: {
        width: '50%',
        borderRight: '1px solid #cbd5e1',
        padding: 12,
    },
    rightSection: {
        width: '50%',
        padding: 12,
        backgroundColor: '#eef2ff'
    },
    fieldRow: {
        flexDirection: 'row',
        marginBottom: 6,
        alignItems: 'center'
    },
    fieldLabel: {
        width: '40%',
        fontSize: 8,
        fontWeight: 'bold',
        color: '#334155'
    },
    fieldValue: {
        width: '60%',
        fontSize: 8,
        padding: 4,
        border: '1px solid #cbd5e1',
        borderRadius: 2,
        backgroundColor: '#ffffff'
    },
    tableTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 6,
        color: '#334155'
    },
    table: {
        border: '1px solid #cbd5e1',
        borderRadius: 2,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#eef2ff',
        borderBottom: '1px solid #cbd5e1',
        fontWeight: 'bold'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottom: '1px solid #e2e8f0'
    },
    tableCell: {
        padding: 4,
        paddingVertical: 5,
        fontSize: 7,
        borderRight: '1px solid #cbd5e1'
    },
    statRow: {
        flexDirection: 'row',
        marginBottom: 8,
        alignItems: 'center'
    },
    statLabel: {
        width: '55%',
        fontSize: 10,
        fontWeight: 'bold',
        color: '#334155'
    },
    statValue: {
        width: '45%',
        fontSize: 16,
        fontWeight: 'bold',
        color: '#0f172a'
    },
    statUnit: {
        fontSize: 7,
        color: '#475569'
    },
    portSection: {
        flexDirection: 'row',
        padding: 12,
        borderBottom: '1px solid #cbd5e1',
        gap: 10
    },
    portField: {
        flex: 1
    },
    portLabel: {
        fontSize: 7,
        fontWeight: 'bold',
        marginBottom: 4,
        color: '#334155'
    },
    portInput: {
        fontSize: 8,
        padding: 4,
        border: '1px solid #cbd5e1',
        borderRadius: 2,
        backgroundColor: '#ffffff'
    },
    carbonSection: {
        padding: 9
    },
    carbonTable: {
        border: '1px solid #cbd5e1',
        borderRadius: 2
    },
    carbonHeaderRow: {
        flexDirection: 'row',
        backgroundColor: '#eef2ff',
        borderBottom: '1px solid #cbd5e1'
    },
    carbonHeaderCell: {
        padding: 4,
        fontSize: 6,
        fontWeight: 'bold',
        borderRight: '1px solid #cbd5e1',
        color: '#334155'
    },
    carbonFooter: {
        flexDirection: 'row',
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #cbd5e1',
        fontWeight: 'bold'
    },
    masterTableHeader: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#334155'
    }
});

const SustainabilityPdf = ({ formData, processedCargoDetails, cargoPartyName }) => {
    // Calculate totals from carbonRows
    const calculateTotals = () => {
        if (!formData?.carbonRows || formData.carbonRows.length === 0) {
            return {
                totalEmission: 0,
                seaEtsEmission: 0,
                portEtsEmission: 0,
                totalEuEts: 0,
                totalEuEtsExp: 0
            };
        }

        return formData.carbonRows.reduce((acc, row) => {
            return {
                totalEmission: acc.totalEmission + (parseFloat(row.totalEmission) || 0),
                seaEtsEmission: acc.seaEtsEmission + (parseFloat(row.seaEtsEmission) || 0),
                portEtsEmission: acc.portEtsEmission + (parseFloat(row.portEtsEmission) || 0),
                totalEuEts: acc.totalEuEts + (parseFloat(row.totalEuEts) || 0),
                totalEuEtsExp: acc.totalEuEtsExp + (parseFloat(row.totalEuEtsExp) || 0)
            };
        }, {
            totalEmission: 0,
            seaEtsEmission: 0,
            portEtsEmission: 0,
            totalEuEts: 0,
            totalEuEtsExp: 0
        });
    };

    const totals = calculateTotals();

    return (
        <View style={styles.page}>
            <Text style={styles.header}>Sustainability Details</Text>
            <View style={styles.container}>
                {/* Top Section */}
                <View style={styles.topSection}>
                    {/* Left Section - Basic Info */}
                    <View style={styles.leftSection}>
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Voyage Number</Text>
                            <Text style={styles.fieldValue}>{formData?.voyageNumber || '-'}</Text>
                        </View>
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Vessel Name</Text>
                            <Text style={styles.fieldValue}>{formData?.vesselName || '-'}</Text>
                        </View>
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Vessel CP Date</Text>
                            <Text style={styles.fieldValue}>
                                {formData?.vesselCpDate
                                    ? moment(formData.vesselCpDate).format('DD/MM/YYYY')
                                    : '-'
                                }
                            </Text>
                        </View>
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Cargo Party</Text>
                            <Text style={styles.fieldValue}>{cargoPartyName || '-'}</Text>
                        </View>
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Cargo CP Date</Text>
                            <Text style={styles.fieldValue}>{formData?.cargoCpDate
                                ? moment(formData.cargoCpDate).format('DD/MM/YYYY')
                                : '-'
                            }</Text>
                        </View>

                        {/* Fuel CF Selector Table */}
                        <Text style={styles.tableTitle}>Fuel CF Selector</Text>
                        <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.tableCell, { width: '15%' }]}>Type</Text>
                                <Text style={[styles.tableCell, { width: '30%' }]}>Description</Text>
                                <Text style={[styles.tableCell, { width: '35%' }]}>MEPC Name</Text>
                                <Text style={[styles.tableCell, { width: '20%', borderRight: 'none' }]}>CF Value</Text>
                            </View>
                            {formData?.fuelRows && formData.fuelRows.length > 0 ? (
                                formData.fuelRows.map((row, idx) => (
                                    <View key={idx} style={[styles.tableRow, idx === formData.fuelRows.length - 1 ? { borderBottomWidth: 0 } : null]}>
                                        <Text style={[styles.tableCell, { width: '15%' }]}>{row.type || '-'}</Text>
                                        <Text style={[styles.tableCell, { width: '30%' }]}>{row.description || '-'}</Text>
                                        <Text style={[styles.tableCell, { width: '35%' }]}>{row.selectedFuel || '-'}</Text>
                                        <Text style={[styles.tableCell, { width: '20%', borderRight: 'none' }]}>
                                            {getCfValue(row.selectedFuel)}
                                        </Text>
                                    </View>
                                ))
                            ) : (
                                <View style={styles.tableRow}>
                                    <Text style={[styles.tableCell, { width: '100%', borderRight: 'none' }]}>No fuel data available</Text>
                                </View>
                            )}
                        </View>
                    </View>

                    {/* Right Section - Summary Stats */}
                    <View style={styles.rightSection}>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Total Cargo Carried</Text>
                            <Text style={styles.statValue}>
                                {processedCargoDetails?.totalCargoCarried?.toFixed(2) || '0.00'}
                                <Text style={styles.statUnit}> MT</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Total Fuel Consumed</Text>
                            <Text style={styles.statValue}>
                                {processedCargoDetails?.totalFuelConsumed?.toFixed(2) || '0.00'}
                                <Text style={styles.statUnit}> MT</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Total Distance Sailed</Text>
                            <Text style={styles.statValue}>
                                {processedCargoDetails?.totalDistanceSailed?.toFixed(2) || '0.00'}
                                <Text style={styles.statUnit}> NM</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Total CO₂ Emitted</Text>
                            <Text style={styles.statValue}>
                                {r2(processedCargoDetails?.totalCarbonConsumed)}
                                <Text style={styles.statUnit}> MT</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Transport Work</Text>
                            <Text style={styles.statValue}>
                                {r2(processedCargoDetails?.transportWork)}
                                <Text style={styles.statUnit}> t-mi</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>EEOI</Text>
                            <Text style={styles.statValue}>
                                {processedCargoDetails?.eeoi?.toFixed(2) || '0.00'}
                                <Text style={styles.statUnit}> gCO₂/t-mi</Text>
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Total ETS Cost</Text>
                            <Text style={styles.statValue}>
                                {r2(processedCargoDetails?.totalEuEtsExp)}
                            </Text>
                        </View>
                        <View style={styles.statRow}>
                            <Text style={styles.statLabel}>Agreed ETS CO₂ (MT)</Text>
                            <Text style={styles.statValue}>
                                {r2(formData?.agreedEtsCo2)}
                            </Text>
                        </View>
                        <View style={[styles.statRow, { marginBottom: 0 }]}>
                            <Text style={styles.statLabel}>Agreed ETS Cost</Text>
                            <Text style={styles.statValue}>
                                {r2(formData?.agreedEtsCost)}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Port Section */}
                <View style={styles.portSection}>
                    <View style={styles.portField}>
                        <Text style={styles.portLabel}>Pre-Voyage Port of Call</Text>
                        <Text style={styles.portInput}>{formData?.preVoyagePort || '-'}</Text>
                    </View>
                    <View style={styles.portField}>
                        <Text style={styles.portLabel}>Post-Voyage Port of Call</Text>
                        <Text style={styles.portInput}>{formData?.postVoyagePort || '-'}</Text>
                    </View>
                </View>

                {/* Carbon Calculator Table */}
                <View style={styles.carbonSection}>
                    <Text style={styles.tableTitle}>Carbon Emissions Analyzer</Text>
                    <View style={styles.carbonTable}>
                        {/* Headers */}
                        <View style={styles.carbonHeaderRow}>
                            <Text style={[styles.carbonHeaderCell, { width: '11%' }]}>Port</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '8%' }]}>Total Emission (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '7%' }]}>Phase-In (%)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '9%' }]}>Sea Emission (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '7%' }]}>Sea ETS %</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '10%' }]}>Sea ETS Emission (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '9%' }]}>Port Emission (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '7%' }]}>Port ETS %</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '10%' }]}>Port ETS Emission (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '9%' }]}>Total EU ETS (MT)</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '9%' }]}>CO₂ Price/MT</Text>
                            <Text style={[styles.carbonHeaderCell, { width: '12%', borderRight: 'none' }]}>Total EU ETS Exp</Text>
                        </View>

                        {/* Data Rows */}
                        {formData?.carbonRows && formData.carbonRows.length > 0 ? (
                            formData.carbonRows.map((row, idx) => (
                                <View key={idx} style={[styles.tableRow, idx === formData.carbonRows.length - 1 ? { borderBottomWidth: 0 } : null]}>
                                    <Text style={[styles.tableCell, { width: '11%' }]}>
                                        {row.port || 0}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '8%' }]}>
                                        {r2(row.totalEmission)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '7%' }]}>
                                        {row.phaseInPercent || 0}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '9%' }]}>
                                        {r2(row.seaEmission)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '7%' }]}>
                                        {row.seaEtsPercent || 0}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '10%' }]}>
                                        {r2(row.seaEtsEmission)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '9%' }]}>
                                        {r2(row.portEmission)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '7%' }]}>
                                        {row.portEtsPercent || 0}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '10%' }]}>
                                        {r2(row.portEtsEmission)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '9%' }]}>
                                        {r2(row.totalEuEts)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '9%' }]}>
                                        {r2(row.co2PricePerMT)}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '12%', borderRight: 'none' }]}>
                                        {r2(row.totalEuEtsExp)}
                                    </Text>
                                </View>
                            ))
                        ) : (
                            <View style={styles.tableRow}>
                                <Text style={[styles.tableCell, { width: '100%', borderRight: 'none' }]}>
                                    No carbon data available
                                </Text>
                            </View>
                        )}

                        {/* Footer */}
                        {formData?.carbonRows && formData.carbonRows.length > 0 && (
                            <View style={styles.carbonFooter}>
                                <Text style={[styles.tableCell, { width: '11%' }]}></Text>
                                <Text style={[styles.tableCell, { width: '8%' }]}>
                                    {totals.totalEmission?.toFixed(2)}
                                </Text>
                                <Text style={[styles.tableCell, { width: '23%', textAlign: 'right' }]}>
                                    Totals:
                                </Text>
                                <Text style={[styles.tableCell, { width: '10%' }]}>
                                    {r2(totals.seaEtsEmission)}
                                </Text>
                                <Text style={[styles.tableCell, { width: '16%' }]}></Text>
                                <Text style={[styles.tableCell, { width: '10%' }]}>
                                    {r2(totals.portEtsEmission)}
                                </Text>
                                <Text style={[styles.tableCell, { width: '9%' }]}>
                                    {totals.totalEuEts?.toFixed(2)}
                                </Text>
                                <Text style={[styles.tableCell, { width: '9%' }]}></Text>
                                <Text style={[styles.tableCell, { width: '12%', borderRight: 'none' }]}>
                                    {totals.totalEuEtsExp?.toFixed(2)}
                                </Text>
                            </View>
                        )}
                    </View>
                </View>
            </View>
        </View>
    );
};

export default SustainabilityPdf;